import {KeyboardEventHandler} from 'react'

const BACKSPACE_KEY = 'Backspace'

/**
 * 자동 탭 이동을 지원하는 KeyUp 이벤트 핸들러 생성 함수
 * - 정상작동의 보장을 위해서는 아래의 규칙으로 input name을 지정해야 합니다.
 * - `${namePrefix}-${index}` (ex: cardNumber-1)
 * - input name 은 고유해야합니다.
 * @example
 * function CardNumbers() {
 *   const handleKeyUp = createKeyUpWithAutoTabEventHandler(4)
 *
 *   return (
 *      <>
 *          <InputNumbers name="cardNumber-1" maxLength={4} onKeyUp={handleKeyUp} />
            <InputNumbers name="cardNumber-2" maxLength={4} onKeyUp={handleKeyUp} />
            <InputNumbers name="cardNumber-3" maxLength={4} onKeyUp={handleKeyUp} />
            <InputNumbers name="cardNumber-4" maxLength={4} onKeyUp={handleKeyUp} />
 *      </>
 *   )
 * }
 * @param {number} numOfFields Input Field 의 수
 * @returns KeyboardEventHandler<HTMLInputElement>
 */
export const createAutoTabEventHandler =
    (lastFieldIndex = 0, wrapper: Element | Document = document): KeyboardEventHandler<HTMLInputElement> =>
    (evt) => {
        if (lastFieldIndex === 0 || !wrapper) {
            return
        }
        const {maxLength, name, value} = evt.currentTarget
        const [fieldName, fieldIndex] = name.split('-')

        // name index가 유효하지 않은 경우 동작 X
        if (!fieldIndex) {
            return
        }
        const currentIndex = parseInt(fieldIndex, 10)

        if (value.length >= maxLength) {
            if (currentIndex < lastFieldIndex) {
                const nextSibling = wrapper.querySelector<HTMLInputElement>(
                    `input[name="${fieldName}-${currentIndex + 1}"]`,
                )
                nextSibling?.focus()
            }
        } else if (value.length === 0 && evt.key === BACKSPACE_KEY) {
            if (currentIndex > 0) {
                const prevSibling = wrapper.querySelector<HTMLInputElement>(
                    `input[name="${fieldName}-${currentIndex - 1}"]`,
                )
                prevSibling?.focus()
            }
        }
    }

/** 외부 CDN을 비동기적으로 Load하는 함수 */
export function loadExternalScript({
    sourceAddress,
    onLoad,
    integrity,
    crossOrigin,
    id,
    type,
    async,
}: {
    sourceAddress: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onLoad: Maybe<(this: GlobalEventHandlers, event: Event) => any>
} & Partial<Pick<HTMLScriptElement, 'crossOrigin' | 'integrity' | 'id' | 'type' | 'async'>>): HTMLScriptElement {
    const script = document.createElement('script')

    integrity && (script.integrity = integrity)
    crossOrigin && (script.crossOrigin = crossOrigin)
    id && (script.id = id)
    type && (script.type = type)
    async && (script.async = async)

    script.src = sourceAddress
    script.onload = onLoad

    document.body.appendChild(script)
    return script
}
